// src/pages/AdminDashboard.jsx
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/axios";
import toast from "react-hot-toast";
import { FaEdit, FaTrash } from "react-icons/fa";
import AddDealer from "./admin/AddDealer";
import {
  FaUsers,
  FaBalanceScale,
  FaHome,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaUserShield,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaWeightHanging,
} from "react-icons/fa";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const isSuper = localStorage.getItem("adminRole") === "super";
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tableView, setTableView] = useState("dealers");
  const [showAddDealer, setShowAddDealer] = useState(false);

  const [gstData, setGstData] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Unregistered dealers (SOTA dealers not assigned by admin)
  const [unregDealers, setUnregDealers] = useState([]);
  const [unregLoading, setUnregLoading] = useState(false);

  const [dashboardStats, setDashboardStats] = useState({
    totalDealers: 0,
    totalUsers: 0,
    totalKgs: 0,
    totalSalesEntries: 0,
    adminDealers: 0,
    topDistricts: [],
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [talukaFilter, setTalukaFilter] = useState("");
  const [statFilter, setStatFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 25;

  const statsToastShown = useRef(false);
  const [dealerToDelete, setDealerToDelete] = useState(null); // ← now using state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserHistory, setSelectedUserHistory] = useState([]);

  const openHistoryModal = async (customer) => {
    if (!customer?._id) return;

    setSelectedUser(customer);
    setHistoryModalOpen(true);
    setSelectedUserHistory([]);

    try {
      const res = await api.get(`/sales/user/${customer._id}/history`);
      if (res.data?.success) {
        setSelectedUserHistory(res.data.history || []);
      } else {
        setSelectedUserHistory([]);
        toast.error(res.data?.message || "No sales history found");
      }
    } catch (err) {
      console.error("Failed to load customer sales:", err);
      toast.error("Failed to load sales history");
      setSelectedUserHistory([]);
    }
  };

  const handleDeleteDealer = async () => {
    if (!dealerToDelete) return;

    const id = dealerToDelete._id || dealerToDelete.id;

    try {
      await api.delete(`/admin/dealers/${id}`);
      toast.success("Dealer deleted");

      setDealers((prev) => prev.filter((d) => (d._id || d.id) !== id));
      setFilteredRows((prev) => prev.filter((d) => (d._id || d.id) !== id));
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete dealer");
    } finally {
      setDealerToDelete(null); // close modal
    }
  };

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  useEffect(() => {
    const fetchGoogleScriptData = async () => {
      try {
        const res = await fetch(
          "https://script.googleusercontent.com/macros/echo?user_content_key=AehSKLjSHaF_3heUqq-f4niuXypH7hExDF2l7_ki-QryxXjRIzpxo8pciu4qQxR5aA4yi3wciJk7W3HUxc48AuJmvhbg9CPhBiThETGKNsA6Oyx7qUfZZg3sgC2vxwCgilChTlt7L9Qx2p23gmt_wG1VqAL3QdrlwDF5kp_NqorTePHNa5oYMp7BxnJUi_6SaTLTwzIM7SO0WHrew2DtMF8PxvjaegGfA3LJfLglP_gPKahcIkePpDv0DvR7oV4EIa9yq3Mga22npBhVrG4ASU2vxhMkXTVXYK496kJEa269"
        );
        const data = await res.json();
        setGstData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Google Script API failed:", error);
      }
    };
    fetchGoogleScriptData();
  }, []);

  useEffect(() => {
    const fetchDealers = async () => {
      try {
        const res = await api.get("/admin/all-dealers");
        if (res.data?.success) {
          const list = res.data.dealers || [];
          setDealers(list);
          setFilteredRows(list);
        }
      } catch (err) {
        // Don't redirect on a network/cold-start error — that caused a
        // login-redirect loop. Only a real 401 (handled by the axios
        // interceptor) should log the user out. Here just inform + retry.
        if (err?.response?.status === 401) {
          toast.error("Session expired. Please login again.");
        } else {
          toast.error("Could not load dealers — server waking up, retrying…");
          setTimeout(() => fetchDealers(), 4000); // retry once cold start clears
        }
      } finally {
        // Show the dashboard as soon as the main table data is in — don't
        // keep the full-screen loader waiting on the slower stats call.
        setLoading(false);
      }
    };
    fetchDealers();
  }, [navigate]);

  const fetchUnregistered = async () => {
    setUnregLoading(true);
    try {
      const res = await api.get("/admin/unregistered-dealers");
      setUnregDealers(res.data?.dealers || []);
    } catch {
      setUnregDealers([]);
    } finally {
      setUnregLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await api.get("/admin/all-users");
      if (res.data?.success) {
        const rawUsers = res.data.users || [];
        const dealerUsers = rawUsers.filter((user) =>
          dealers.some(
            (d) =>
              (d._id === user.dealerId || d.id === user.dealerId) &&
              d.type === "dealer"
          )
        );

        const usersWithTotal = await Promise.all(
          dealerUsers.map(async (user) => {
            try {
              const historyRes = await api.get(`/sales/user/${user._id}/history`);
              if (historyRes.data?.success) {
                const total = historyRes.data.history.reduce(
                  (sum, h) => sum + (h.salesCount || 0),
                  0
                );
                return { ...user, totalSales: total };
              }
            } catch { }
            return { ...user, totalSales: 0 };
          })
        );

        setAllUsers(usersWithTotal);
      } else {
        toast.error("Failed to load customers");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error loading all customers");
    } finally {
      setUsersLoading(false);
    }
  };

  const dealersOnly = useMemo(() => {
    return dealers.filter((d) => (d.type || "dealer") !== "admin");
  }, [dealers]);

  const dealerEntriesFromAllDealers = useMemo(() => {
    return dealersOnly.reduce((acc, d) => {
      const v =
        Number(d.totalSalesEntries) ||
        Number(d.salesEntries) ||
        Number(d.salesCount) ||
        Number(d.totalSalesCount) ||
        0;
      return acc + v;
    }, 0);
  }, [dealersOnly]);

  const hasEntryCountInAllDealers = useMemo(() => {
    return dealersOnly.some((d) => {
      const v =
        Number(d.totalSalesEntries) ||
        Number(d.salesEntries) ||
        Number(d.salesCount) ||
        Number(d.totalSalesCount) ||
        0;
      return v > 0;
    });
  }, [dealersOnly]);

  const dealersWithSalesCount = useMemo(() => {
    return dealersOnly.filter((d) => Number(d.totalSales) > 0).length;
  }, [dealersOnly]);

  const salesEntriesCard = useMemo(() => {
    if (hasEntryCountInAllDealers) {
      return { title: "Sales Entries", value: dealerEntriesFromAllDealers };
    }
    return { title: "Customers With Sales", value: dealersWithSalesCount };
  }, [hasEntryCountInAllDealers, dealerEntriesFromAllDealers, dealersWithSalesCount]);

  const totalDealerUsers = useMemo(() => {
    return dealersOnly.reduce((sum, d) => sum + (Number(d.totalUsers) || 0), 0);
  }, [dealersOnly]);

  const totalDealerKgs = useMemo(() => {
    return dealersOnly.reduce((sum, d) => sum + (Number(d.totalSales) || 0), 0);
  }, [dealersOnly]);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      // Runs in the background — stat cards fill in when ready. Does NOT gate
      // the full-screen loader (that's tied to the dealers fetch).
      try {
        const res = await api.post("/admin/dashboard-stats");
        if (res.data?.success) {
          setDashboardStats(res.data.stats || dashboardStats);
        }
      } catch (err) {
        console.error("Failed to load dashboard stats:", err);
      }
    };
    fetchDashboardStats();
  }, []);

  const baseList = useMemo(() => {
    if (tableView === "admins") return dealers.filter((d) => d.type === "admin");
    if (tableView === "all-users") return allUsers;
    return dealersOnly;
  }, [dealers, dealersOnly, allUsers, tableView]);

  const districts = useMemo(
    () =>
      tableView !== "admins"
        ? [...new Set(baseList.map((d) => d.district).filter(Boolean))].sort()
        : [],
    [baseList, tableView]
  );

  const talukas = useMemo(() => {
    if (!districtFilter) return [];
    return [
      ...new Set(
        baseList
          .filter((d) => d.district === districtFilter)
          .map((d) => d.taluka)
          .filter(Boolean)
      ),
    ].sort();
  }, [baseList, districtFilter]);

  useEffect(() => {
    let data = [...baseList];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();

      data = data.filter((item) => {
        const name = (item.name || item.partyName || "").toLowerCase();
        const mobile = (item.mobile || "").toString();
        const district = (item.district || "").toLowerCase();
        const taluka = (item.taluka || "").toLowerCase();
        const dealer = tableView === "all-users" ? (item.dealerName || "").toLowerCase() : "";

        return (
          name.includes(q) ||
          mobile.includes(q) ||
          district.includes(q) ||
          taluka.includes(q) ||
          dealer.includes(q)
        );
      });
    }

    if (districtFilter) {
      data = data.filter((item) => item.district === districtFilter);
    }

    if (talukaFilter) {
      data = data.filter((item) => item.taluka === talukaFilter);
    }

    if (tableView === "dealers") {
      if (statFilter === "users") {
        data = data.filter((d) => Number(d.totalUsers || 0) > 0);
      }
      if (statFilter === "sales") {
        data = data.filter((d) => Number(d.totalSales || 0) > 0);
      }
    }

    setFilteredRows(data);
    setCurrentPage(1);
  }, [searchQuery, districtFilter, talukaFilter, statFilter, baseList, tableView]);

  const sortedRows = useMemo(() => {
    let sortable = [...filteredRows];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aVal = a[sortConfig.key] ?? "";
        let bVal = b[sortConfig.key] ?? "";

        if (["totalUsers", "totalSales"].includes(sortConfig.key)) {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        }

        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredRows, sortConfig]);

  const totalPages = Math.ceil(sortedRows.length / rowsPerPage);
  const currentRows = sortedRows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handleSort = (key) => {
    if (tableView === "all-users" && !["name", "mobile", "dealerName", "district", "totalSales"].includes(key)) return;
    if (tableView === "admins" && ["totalUsers", "totalSales"].includes(key)) return;

    setSortConfig((current) => {
      if (current.key === key) {
        if (current.direction === "asc") return { key, direction: "desc" };
        return { key: null, direction: "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-gray-400" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="text-yellow-600" />
    ) : (
      <FaSortDown className="text-yellow-600" />
    );
  };

  const resetFilters = () => {
    setSearchQuery("");
    setDistrictFilter("");
    setTalukaFilter("");
    setStatFilter("");
    setSortConfig({ key: null, direction: "asc" });
  };

  const showDealersWith = (nextStatFilter = "") => {
    setTableView("dealers");
    resetFilters();
    setStatFilter(nextStatFilter);
  };

  const logout = () => {
    // Only remove authentication-related keys (safer than clear())
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("dealer");
    localStorage.removeItem("userType");
    localStorage.removeItem("loginTimestamp");

    // Optional: clear any other auth tokens if you have them
    // localStorage.removeItem("token");

    toast.success("Logged out successfully", {
      duration: 3000,
      position: "top-center",
    });

    // Use replace: true → removes current (protected) page from history
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl font-medium">Loading Admin Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-80 bg-white border-r shadow-2xl transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="px-6 py-5 border-b border-yellow-600 flex items-center justify-center">
          <img
            src="/images/logo.png"
            alt="Logo"
            className="h-16 w-auto object-contain cursor-pointer"
            onClick={() => showDealersWith("")}
          />
        </div>

        <div className="px-5 py-6 flex-1">
          <div className="space-y-3">
            <StatCard
              title="Total Dealers"
              value={dashboardStats.totalDealers}
              icon={<FaUserShield />}
              onClick={() => {
                showDealersWith("");
                setSidebarOpen(false);
              }}
            />
            <StatCard
              title="Total Customers"
              value={totalDealerUsers}
              icon={<FaUsers />}
              onClick={() => {
                setTableView("all-users");
                fetchAllUsers();
                resetFilters();
                setSidebarOpen(false);
              }}
            />
            <StatCard
              title="Total Sales (KG)"
              value={totalDealerKgs.toLocaleString() + " KG"}
              icon={<FaWeightHanging />}
              onClick={() => {
                showDealersWith("sales");
                setSidebarOpen(false);
              }}
            />
            <StatCard
              title={salesEntriesCard.title}
              value={salesEntriesCard.value}
              icon={<FaBalanceScale />}
              onClick={() => {
                showDealersWith("sales");
                setSidebarOpen(false);
              }}
            />
            <StatCard
              title="Admins"
              value={dashboardStats.adminDealers}
              icon={<FaUserShield />}
              onClick={() => {
                setTableView("admins");
                resetFilters();
                setSidebarOpen(false);
              }}
            />
          </div>
        </div>

        <div className="px-5 py-5 border-t">
          <button
            onClick={() => {
              setShowAddDealer(true);
              setSidebarOpen(false);
            }}
            className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3.5 px-5 rounded-xl flex items-center justify-center gap-2 shadow-md transition text-base"
          >
            <span className="text-xl leading-none">+</span> Add New Dealer
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main content */}
      <main className="flex-1 p-4 lg:p-6">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-40 bg-white border-b shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg bg-yellow-600 text-white"
              >
                {sidebarOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
              </button>
              <h1 className="text-lg font-bold text-gray-900">Admin Panel</h1>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/admin/global-rewards")}
                className="p-2 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-semibold"
                title="Rewards"
              >
                🎁
              </button>
              {isSuper && (
                <button
                  onClick={() => navigate("/admin/manage-admins")}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-semibold"
                  title="Manage Admins"
                >
                  👤 Admins
                </button>
              )}
              <button
                onClick={logout}
                className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700"
                title="Logout"
              >
               <FaSignOutAlt size={20} /> <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Desktop top actions */}
        <div className="mb-5 flex justify-end gap-3 max-md:hidden">
          <button
            onClick={() => navigate("/admin/global-rewards")}
            className="h-11 px-4 rounded-xl bg-amber-500 text-white flex items-center gap-2 hover:bg-amber-600 transition shadow-sm"
          >
            <span className="font-semibold">🎁 Rewards</span>
          </button>
          {isSuper && (
            <button
              onClick={() => navigate("/admin/manage-admins")}
              className="h-11 px-4 rounded-xl bg-gray-800 text-white flex items-center gap-2 hover:bg-gray-900 transition shadow-sm"
            >
              <span className="font-semibold">👤 Manage Admins</span>
            </button>
          )}
          <button
                onClick={logout}
                className="h-10 w-10 sm:w-auto sm:px-5 rounded-xl bg-red-600 text-white hover:bg-red-700 transition shadow-sm font-semibold flex items-center justify-center gap-2"
                title="Logout"
              >
                <FaSignOutAlt size={18} />
                <span className="hidden sm:inline">Logout</span>
              </button>
        </div>

        {/* View switcher */}
        <div className="mb-4 flex items-center gap-2 flex-wrap max-md:pt-[10px]">
          <button
            onClick={() => {
              setTableView("dealers");
              resetFilters();
            }}
            className={`h-11 px-5 max-md:text-[12px] rounded-xl border font-semibold transition ${tableView === "dealers" ? "bg-yellow-600 text-white border-yellow-600" : "bg-white hover:bg-gray-50"
              }`}
          >
            Dealers
          </button>
          <button
            onClick={() => {
              setTableView("all-users");
              fetchAllUsers();
              resetFilters();
            }}
            className={`h-11 px-5 rounded-xl max-md:text-[12px] border font-semibold transition ${tableView === "all-users" ? "bg-yellow-600 text-white border-yellow-600" : "bg-white hover:bg-gray-50"
              }`}
          >
            Total Customers
          </button>
          <button
            onClick={() => {
              setTableView("admins");
              resetFilters();
            }}
            className={`h-11 px-5 rounded-xl max-md:text-[12px] border font-semibold transition ${tableView === "admins" ? "bg-yellow-600 text-white border-yellow-600" : "bg-white hover:bg-gray-50"
              }`}
          >
            Admins
          </button>
          <button
            onClick={() => {
              setTableView("unregistered");
              fetchUnregistered();
              resetFilters();
            }}
            className={`h-11 px-5 rounded-xl max-md:text-[12px] border font-semibold transition ${tableView === "unregistered" ? "bg-yellow-600 text-white border-yellow-600" : "bg-white hover:bg-gray-50"
              }`}
          >
            Unregistered
          </button>
        </div>

        {/* Unregistered dealers — read-only table */}
        {tableView === "unregistered" && (
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden mb-6">
            <div className="overflow-auto" style={{ maxHeight: "62vh" }}>
              {unregLoading ? (
                <div className="p-12 text-center text-gray-500">Loading…</div>
              ) : unregDealers.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  No unregistered dealers yet.
                </div>
              ) : (
                <table className="w-full min-w-[760px] border-collapse">
                  <thead className="bg-gray-100 sticky top-0 z-10 border-b">
                    <tr className="text-left text-gray-700">
                      <th className="px-4 py-3">#</th>
                      <th className="px-4 py-3">Firm / Name</th>
                      <th className="px-4 py-3">Mobile</th>
                      <th className="px-4 py-3">GST</th>
                      <th className="px-4 py-3">District</th>
                      <th className="px-4 py-3 text-center">Customers</th>
                      <th className="px-4 py-3 text-center">Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unregDealers.map((d, i) => (
                      <tr key={d.id} className="border-b hover:bg-yellow-50 transition" style={{ height: 52 }}>
                        <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                        <td className="px-4 py-3 font-semibold text-gray-900 uppercase">
                          {d.name}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{d.mobile}</td>
                        <td className="px-4 py-3 text-gray-700">{d.gst || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{d.district || "—"}</td>
                        <td className="px-4 py-3 text-center">{d.totalUsers || 0}</td>
                        <td className="px-4 py-3 text-center font-extrabold text-yellow-700">
                          {(d.totalSales || 0).toLocaleString()} KG
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tableView !== "unregistered" && (
        <>
        {/* Filters */}
        <div className="bg-white p-5 rounded-2xl border shadow-sm mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="relative">
              <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${tableView === "admins" ? "admin" : "dealer"} name, mobile, location...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            {tableView !== "admins" && (
              <>
                <select
                  value={districtFilter}
                  onChange={(e) => {
                    setDistrictFilter(e.target.value);
                    setTalukaFilter("");
                  }}
                  className="h-11 border border-gray-300 rounded-xl px-4 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">All Districts</option>
                  {districts.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>

                {tableView !== "all-users" && districtFilter && (
                  <select
                    value={talukaFilter}
                    onChange={(e) => setTalukaFilter(e.target.value)}
                    className="h-11 border border-gray-300 rounded-xl px-4 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">All Talukas</option>
                    {talukas.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                )}

                <button
                  onClick={resetFilters}
                  className="h-11 bg-red-500 hover:bg-red-600 text-white rounded-xl px-4 transition font-semibold"
                >
                  Reset
                </button>
              </>
            )}
          </div>
        </div>

        {/* Table / Cards container */}
        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-auto" style={{ height: "calc(55vh - 60px)" }}>
            {usersLoading && tableView === "all-users" ? (
              <div className="flex items-center justify-center h-full text-gray-500">Loading customers...</div>
            ) : (
              <table className="w-full min-w-[980px] border-collapse">
                <thead className="bg-gray-100 sticky top-0 z-20 border-b">
                  <tr>
                    {tableView === "all-users" ? (
                      <>
                        <th className="px-6 py-4 cursor-pointer" onClick={() => handleSort("district")}>
                          <div className="flex items-center gap-2">District {getSortIcon("district")}</div>
                        </th>
                        <th className="px-6 py-4 cursor-pointer" onClick={() => handleSort("dealerName")}>
                          <div className="flex items-center gap-2">Dealer Name {getSortIcon("dealerName")}</div>
                        </th>
                        <th className="px-6 py-4 text-left cursor-pointer" onClick={() => handleSort("name")}>
                          <div className="flex items-center gap-2">
                            Customer Name {getSortIcon("name")}
                          </div>
                        </th>
                        <th className="px-6 py-4 cursor-pointer" onClick={() => handleSort("mobile")}>
                          <div className="flex items-center gap-2">Mobile {getSortIcon("mobile")}</div>
                        </th>


                        <th
                          className="px-6 py-4 text-center font-semibold cursor-pointer whitespace-nowrap"
                          onClick={() => handleSort("totalSales")}
                        >
                          <div className="flex items-center justify-center gap-2">
                            Total Sales (KG) {getSortIcon("totalSales")}
                          </div>
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-4 cursor-pointer" onClick={() => handleSort("district")}>
                          <div className="flex items-center gap-2">District {getSortIcon("district")}</div>
                        </th>
                        <th className="px-6 py-4 cursor-pointer" onClick={() => handleSort("district")}>
                          <div className="flex items-center gap-2">Taluka {getSortIcon("district")}</div>
                        </th>
                        <th className="px-6 py-4 text-left cursor-pointer" onClick={() => handleSort("name")}>
                          <div className="flex items-center gap-2">
                            {tableView === "admins" ? "Admin Name" : "Dealer Firm Name"} {getSortIcon("name")}
                          </div>
                        </th>
                        <th
                          className="px-6 py-4 hidden sm:table-cell cursor-pointer"
                          onClick={() => handleSort("mobile")}
                        >
                          <div className="flex items-center gap-2">Mobile {getSortIcon("mobile")}</div>
                        </th>

                        {tableView !== "admins" && (
                          <>
                            <th
                              className="px-6 py-4 text-center cursor-pointer"
                              onClick={() => handleSort("totalUsers")}
                            >
                              <div className="flex items-center gap-2 justify-center">
                                Users {getSortIcon("totalUsers")}
                              </div>
                            </th>
                            <th
                              className="px-6 py-4 text-center cursor-pointer"
                              onClick={() => handleSort("totalSales")}
                            >
                              <div className="flex items-center gap-2 justify-center">
                                Sales (KG) {getSortIcon("totalSales")}
                              </div>
                            </th>
                            <th className="px-6 py-4 text-center">Actions</th>
                          </>
                        )}
                      </>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {currentRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={tableView === "all-users" ? 5 : tableView === "admins" ? 3 : 5}
                        className="text-center py-16 text-gray-500"
                      >
                        No {tableView === "all-users" ? "customers" : tableView === "admins" ? "admins" : "dealers"} found.
                      </td>
                    </tr>
                  ) : (
                    currentRows.map((item) => {
                      if (tableView === "all-users") {
                        return (
                          <tr
                            key={item._id}
                            onClick={() => openHistoryModal(item)}
                            className="hover:bg-yellow-50 border-b transition cursor-pointer"
                            style={{ height: 56 }}
                          >
                            <td className="px-6 py-3 text-gray-700">{item.district || "—"}</td>

                            <td className="px-6 py-3 text-gray-700 uppercase">{item.dealerName || "—"}</td>
                            <td className="px-6 py-3 font-medium text-gray-900 uppercase">{item.name || "—"}</td>
                            <td className="px-6 py-3 text-gray-700">{item.mobile || "—"}</td>
                            <td className="px-6 py-3 text-center font-semibold text-yellow-700">
                              {(Number(item.totalSales) || 0).toLocaleString("en-IN")} KG
                            </td>
                          </tr>
                        );
                      }

                      return (


                        <tr
                          key={item._id || item.id}
                          onClick={() => navigate(`/dealer-details/${item.id || item._id}`)}
                          className="hover:bg-yellow-50 cursor-pointer border-b transition"
                          style={{ height: 56 }}
                        >

                          <td className="px-6 py-3 text-gray-700">
                            {item.district}
                          </td>
                          <td className="px-6 py-3 text-gray-700">
                            {item.taluka}
                          </td>
                          <td className="px-6 py-3 font-semibold text-gray-900 uppercase">
                            {item.name || item.partyName || "Unnamed"}
                          </td>
                          <td className="px-6 py-3 text-gray-700">{item.mobile || "—"}</td>

                          {tableView !== "admins" && (
                            <>
                              <td className="px-6 py-3 text-center">{item.totalUsers || 0}</td>
                              <td className="px-6 py-3 text-center font-extrabold text-yellow-700">
                                {(item.totalSales || 0).toLocaleString()} KG
                              </td>
                              <td className="px-6 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-center gap-4">
                                  <button
                                    onClick={() => navigate(`/dealer/${item.id || item._id}/rewards`)}
                                    className="text-green-600 hover:text-green-800 font-semibold"
                                  >
                                    Add Rewards
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDealerToDelete(item); // ← fixed
                                    }}
                                    className="text-red-600 hover:text-red-800"
                                    title="Delete"
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4 p-4 pb-28">
            {usersLoading && tableView === "all-users" ? (
              <div className="text-center py-16 text-gray-600">Loading customers…</div>
            ) : currentRows.length === 0 ? (
              <div className="bg-gray-50 rounded-2xl p-10 text-center text-gray-600 shadow-inner">
                No records found
              </div>
            ) : tableView === "all-users" ? (
              currentRows.map((u) => (
                <div
                  key={u._id}
                  onClick={() => openHistoryModal(u)}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 active:bg-yellow-50/60 transition cursor-pointer"
                >
                  <div className="flex justify-between items-start gap-3 mb-3">
                    <h3 className="font-semibold text-base leading-tight text-gray-900">
                      {u.name || "Unnamed Customer"}
                    </h3>
                    <div className="shrink-0">
                      <span className="inline-block bg-yellow-100 text-yellow-800 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                        {(Number(u.totalSales) || 0).toLocaleString("en-IN")} kg
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700">
                    <div>
                      <span className="text-gray-500 text-xs block">Mobile</span>
                      {u.mobile || "—"}
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs block">Dealer</span>
                      {u.dealerName || "—"}
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500 text-xs block">District</span>
                      {u.district || "—"}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              currentRows.map((d) => (
                <div
                  key={d._id || d.id}
                  onClick={() => navigate(`/dealer-details/${d.id || d._id}`)}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 active:bg-yellow-50/60 transition cursor-pointer"
                >
                  <div className="flex justify-between items-start gap-3 mb-3">
                    <div>
                      <h3 className="font-semibold text-base leading-tight text-gray-900">
                        {d.name || d.partyName || "Unnamed"}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">{d.mobile || "—"}</p>
                    </div>
                    {tableView !== "admins" && (
                      <div className="shrink-0">
                        <span className="inline-block bg-yellow-100 text-yellow-800 text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap">
                          {(d.totalSales || 0).toLocaleString()} kg
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap justify-between items-center gap-3 text-sm text-gray-700">
                    <div className="flex items-center gap-1.5">
                      <span>{d.district || "—"}</span>
                      {d.taluka && <span className="text-gray-500">({d.taluka})</span>}
                    </div>
                    {tableView !== "admins" && (
                      <div className="flex items-center gap-1.5">
                        <FaUsers className="text-gray-500" size={14} />
                        <span>{d.totalUsers || 0} users</span>
                      </div>
                    )}
                  </div>

                  {tableView !== "admins" && (
                    <div className="mt-4 pt-3 border-t flex justify-end gap-6 text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dealer/${d.id || d._id}/rewards`);
                        }}
                        className="text-green-600 hover:text-green-800"
                      >
                        Add Reward
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDealerToDelete(d); // ← fixed
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t bg-white px-4 py-3 flex items-center justify-between gap-3 flex-wrap sticky bottom-0 z-10">
              <div className="text-sm text-gray-600">
                Page <b>{currentPage}</b> of <b>{totalPages}</b>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="h-10 px-4 rounded-xl bg-gray-100 border hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="h-10 px-4 rounded-xl bg-gray-100 border hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
                >
                  Prev
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages })
                    .slice(Math.max(currentPage - 3, 0), Math.min(currentPage + 2, totalPages))
                    .map((_, idx) => {
                      const page = Math.max(currentPage - 3, 0) + idx + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`h-10 w-10 rounded-xl border transition font-semibold ${page === currentPage
                            ? "bg-yellow-600 text-white border-yellow-600"
                            : "bg-white hover:bg-gray-100"
                            }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                </div>

                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="h-10 px-4 rounded-xl bg-gray-100 border hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="h-10 px-4 rounded-xl bg-gray-100 border hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
        </>
        )}

        {/* Floating action button - mobile only */}
        <button
          onClick={() => setShowAddDealer(true)}
          className="lg:hidden fixed bottom-6 right-5 z-40 bg-yellow-600 text-white rounded-full w-14 h-14 flex items-center justify-center text-3xl shadow-xl hover:bg-yellow-700 active:scale-95 transition-transform"
          aria-label="Add new dealer"
        >
          +
        </button>

        {showAddDealer && (
          <AddDealer
            gstData={gstData}
            onClose={() => setShowAddDealer(false)}
            onCreated={() => setShowAddDealer(false)}
          />
        )}

        {/* Delete Confirmation Modal */}
        {dealerToDelete && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-sm p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-2">Delete Dealer</h3>
              <p className="text-gray-600 mb-5">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-red-600">
                  {dealerToDelete.name || dealerToDelete.partyName || "this dealer"}
                </span>
                ?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDealerToDelete(null)}
                  className="px-4 py-2 rounded-lg border text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteDealer}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {historyModalOpen && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-6 flex flex-col max-h-[80vh] relative">
              <button
                onClick={() => setHistoryModalOpen(false)}
                className="absolute top-3 right-3 text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition"
                title="Close"
              >
                <FaTimes size={16} />
              </button>

              <h2 className="text-xl font-extrabold mb-4">
                {selectedUser ? `${selectedUser.name}'s Sales` : "Customer Sales"}
              </h2>

              <div className="flex-1 overflow-y-auto mb-4">
                {selectedUserHistory.length === 0 ? (
                  <p className="text-gray-500 text-center py-10">
                    No sales entries found for this customer
                  </p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {selectedUserHistory.map((h, idx) => (
                      <div
                        key={idx}
                        className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex flex-col gap-2"
                      >
                        <p className="text-sm text-gray-500">
                          Date: {h.date ? new Date(h.date).toLocaleDateString("en-IN") : "—"}
                        </p>
                        <p className="font-extrabold text-yellow-700">
                          Sales: {Number(h.salesCount || 0).toLocaleString()} KG
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-yellow-100 border-t border-yellow-300 rounded-xl p-4 flex flex-col gap-1 sticky bottom-0">
                <p className="font-extrabold text-gray-800">
                  Total Sales:{" "}
                  {selectedUserHistory
                    .reduce((sum, h) => sum + Number(h.salesCount || 0), 0)
                    .toLocaleString()}{" "}
                  KG
                </p>
                <p className="font-semibold text-gray-700">Entries: {selectedUserHistory.length}</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ title, value, icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-white rounded-2xl p-4 flex items-center gap-4 border shadow-sm hover:shadow-md hover:border-yellow-200 hover:bg-yellow-50 transition"
    >
      <div className="shrink-0 p-3 bg-yellow-600 text-white rounded-xl text-xl">{icon}</div>
      <div className="min-w-0">
        <p className="text-sm text-gray-600 truncate">{title}</p>
        <p className="text-2xl font-extrabold text-gray-900 mt-0.5 truncate">{value}</p>
      </div>
    </button>
  );
}